import { useDataDogRUM } from "@/composables/useDataDogRUM";
import { useServerStatus } from '@/composables/useServerStatus';
import { useSessionStatus } from '@/composables/useSessionStatus';
import { broadcastAuthEvent, markForceLoggedOut } from '@/composables/useCrossTabAuth';
import router from '@/router';
import { updateBoomtownHeaders } from '@/services/BoomtownHeaders';
import { HttpBaseClient } from '@/services/httpBaseClient';
import { AdminAccessDeniedError, isAdminSuperuser } from '@/utils/adminAccess';
import { Base64 } from '@/utils/Base64';
import { fetchClientInstanceId } from '@/utils/clientInstance';
import { showAuthenticationError } from '@/utils/earlyToast';
import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { ref, watch } from 'vue';
import { useMetaStore } from './meta';
import { usePartnerStore } from './partner';
import { useUserStore } from './user';

export { AdminAccessDeniedError } from '@/utils/adminAccess';

export interface AuthStoreInterface {
    isAuthenticated: boolean;
    isLoggingIn: boolean;
    isLoggingOut: boolean;
    csrfToken: string;
    actingAs: string;
    /** True while login or cross-tab CSRF sync should suppress 401→logout. */
    shouldSuppress401Logout?: () => boolean;
}

/**
 * Login credentials for authentication
 */
export interface LoginCredentials {
    email: string;
    password: string;
    rememberMe?: boolean;
}

/**
 * Login response structure
 */
export interface LoginResponse {
    success: boolean;
    authenticated?: boolean;
    message?: string;
    csrf_token?: string;
    users?: {
        id: string;
        email: string;
        full_name?: string;
        /** Boomtown usertype; Admin/Superuser is `1` */
        role?: number;
        _perms?: number[];
        [key: string]: any;
    };
    xmpp?: {
        password: string;
        [key: string]: any;
    };
    editor_key?: string;
    perm_defs?: {
        [key: string]: number;
    };
    users_group?: {
        [key: string]: number;
    };
    all_access?: string[];
    password_policy?: {
        expiration: string;
        idle_timeout: number;
        lockout: string;
        pattern: string;
        pattern_regex: string;
        rotation: string;
        session_terminate: string;
    };
    screens_url?: string;
    cdn_url?: string;
    users_teams?: any[];
    card_fields_map?: any;
    schema_fields_map?: any;
    custom_case_fields?: any;
    custom_location_fields?: any;
    custom_member_fields?: any;
    custom_users_fields?: any;
};

export interface resetCredentials {
    email: string;
}

export interface changeCredentials {
    email: string;
    password: string;
    confirm_password: string;
    token: string;
}

export interface ResetPasswordResponse {
    success: boolean
    message?: string
    // add any other fields your API returns
}


export const useAuthStore = defineStore('auth', () => {
    const isAuthenticated = ref(false);
    const isInitialized = ref(false);
    const isLoggingOut = ref(false);
    const isLoggingIn = ref(false); // Track if we're currently in the login process
    /** True while SAML ACS post + session confirmation runs; suppresses spurious /login from handleAuthChange */
    const isCompletingSsoLogin = ref(false);
    let loginInitTimeout: number | null = null; // Track the login initialization timeout
    /** Epoch ms until which 401→logout is suppressed after a remote CSRF sync. */
    let csrfSyncGraceUntil = 0;
    /** Prevent concurrent session-recovery attempts after a CSRF race 401. */
    let isRecoveringSession = false;
    const CSRF_SYNC_GRACE_MS = 8_000;
    const POST_LOGIN_401_GRACE_MS = 5_000;
    const csrfToken = ref('');
    const clientInstanceId = ref(fetchClientInstanceId());
    const apiBaseUrl = ref(import.meta.env.VITE_BOOMTOWN_API_HOST);
    const userEmail = ref('');
    const editorKey = ref('');
    const allAccess = ref<string[]>([]);
    const redirectAfterLogin = ref('');
    // Acting-as impersonation — persisted in sessionStorage so it survives SPA navigation
    // but clears when the tab is closed. Value format: "users:<id>"
    const actingAs = ref<string>(sessionStorage.getItem('actingAs') ?? '');

    let initPromise: Promise<void> | null = null;
    // Create base HTTP client for auth-related API calls
    const baseHttpClient = new HttpBaseClient(apiBaseUrl.value);

    const { setUser: setDataDogUser, clearUser: clearDataDogUser } = useDataDogRUM();
    watch(isAuthenticated, (newVal) => {
        if(!newVal) {
            // User is no longer authenticated, clear DataDog user
            clearDataDogUser();
            return;
        }
        setTimeout(() => {
            const userStore = useUserStore();
            setDataDogUser({
                id: userStore.userData?.id ?? null,
                name: userStore.userData?.full_name ?? null,
                email: userStore.userData?.email ?? null,
                org_id: userStore.userData?.object_id ?? null,
                org_name: userStore.orgData?.name ?? null,
            });
        }, 0);
    });

    // Initializes the auth state
    function initialize(): Promise<void> {
        if (isInitialized.value) return Promise.resolve();

        // Don't initialize if we're in the middle of logging out
        if (isLoggingOut.value) return Promise.resolve();

        // If initialization is already in-flight, return the same promise so
        // concurrent callers (App.vue onMounted + router guard) share one request.
        if (initPromise) return initPromise;

        initPromise = (async () => {
            try {
                // If another tab just logged in, adopt its CSRF before the first
                // userStatus so early requests (if any) don't use a stale anonymous token.
                try {
                    const pendingCsrf = sessionStorage.getItem('cxme-pending-csrf');
                    if (pendingCsrf) {
                        csrfToken.value = pendingCsrf;
                        updateBoomtownHeaders(pendingCsrf, clientInstanceId.value);
                        sessionStorage.removeItem('cxme-pending-csrf');
                    }
                } catch { /* ignore */ }

                const response = await refreshCsrfToken();
                if (response) {
                    isAuthenticated.value = response.authenticated || false;

                    if (response.csrf_token) {
                        csrfToken.value = response.csrf_token;
                    }

                    if (response.users?.email) {
                        userEmail.value = response.users.email;
                    }

                    if (response.authenticated) {
                        // Clear cross-tab login-reload debounce so a future login
                        // in another tab can still sync this tab when needed.
                        try {
                            sessionStorage.removeItem('cxme-cross-tab-login-reload');
                        } catch { /* ignore */ }
                        startTokenRefresh();
                    }
                }
            } catch (error) {
                console.error('Failed to initialize auth state:', error);
                isAuthenticated.value = false;
            }

            isInitialized.value = true;
            initPromise = null;
        })();

        return initPromise;
    }

    // Refresh CSRF token
    async function refreshCsrfToken(skipDataLoading: boolean = false, skipPartnerFetch: boolean = false): Promise<LoginResponse | null> {
        const { reportServerError, reportSuccess } = useServerStatus();
        const { reportAuthSuccess } = useSessionStatus();
        const headers: Record<string, string> = {
            'x-boomtown-client-instance-id': clientInstanceId.value,
            'x-request-id': uuidv4(),
            'content-type': 'application/json',
        };

        if (csrfToken.value) {
            headers['x-boomtown-csrf-token'] = csrfToken.value;
        }

        if (actingAs.value) {
            headers['Acting-As'] = actingAs.value;
        }

        try {
            const data = await baseHttpClient.get<LoginResponse>(
                'admin/v4/core/',
                { sAction: 'userStatus' },
                headers
            );

            // Report successful API response to clear any server error state
            reportSuccess();
            
            // Report successful authentication to clear any session error state
            if (data.authenticated) {
                reportAuthSuccess();
            }

            if (data.success) {
                // Only Admin/Superuser (role === 1) may use this app
                if (data.authenticated && data.users && !isAdminSuperuser(data.users)) {
                    console.error('Session denied: user is not an Admin/Superuser', {
                        userId: data.users.id,
                        role: data.users.role,
                    });
                    const deniedToken = data.csrf_token || csrfToken.value;
                    await clearDeniedAdminSession(deniedToken);
                    throw new AdminAccessDeniedError();
                }

                // Defer user store access to avoid circular dependency issues
                setTimeout(async () => {
                    const wasAuthenticated = isAuthenticated.value;
                    isAuthenticated.value = data.authenticated || false;
                    try {
                        const userStore = useUserStore();
                        userStore.persistUserData(
                            data.users,
                            data.perm_defs || null,
                            data.users?._perms || null,
                            data.xmpp || null,
                            data.password_policy || null,
                            data.users_group || null,
                            data.screens_url || "",
                            data.cdn_url || "",
                            data.users_teams || null,
                            data.card_fields_map || null,
                            data.schema_fields_map || null,
                            data.custom_case_fields || null,
                            data.custom_location_fields || null,
                            data.custom_member_fields || null,
                            data.custom_users_fields || null
                        );

                        // Update editorKey in auth store if available
                        if (data.editor_key) {
                            editorKey.value = Base64.decode(data.editor_key);
                        }

                        // Update allAccess in auth store if available
                        if (data.all_access && Array.isArray(data.all_access)) {
                            allAccess.value = data.all_access;
                        }

                        // Only load partner and meta data if not skipping data loading
                        // This prevents conflicts when called from SSO flow
                        if (!skipDataLoading) {
                            // Fetch current partner data after user data is loaded.
                            // Skipped during periodic token refresh (every 60s) since partner type
                            // virtually never changes mid-session — only needed on login/init.
                            if (!skipPartnerFetch && data.authenticated && data.users?.object_id) {
                                try {
                                    // Add a small delay to ensure any cookie updates are processed
                                    await new Promise(resolve => setTimeout(resolve, 50));
                                    
                                    const partnerStore = usePartnerStore();
                                    await partnerStore.fetchCurrentPartner();
                                } catch (partnerError) {
                                    console.error('Failed to fetch current partner data:', partnerError);
                                    // Don't throw here - partner data is not critical for authentication
                                }

                                // Load meta data after successful authentication check
                                try {
                                    const metaStore = useMetaStore();
                                    await Promise.all([
                                        metaStore.loadMetaData(),
                                        metaStore.loadPartnerMetaData()
                                    ]);
                                } catch (metaError) {
                                    console.error('Failed to load meta data on refresh:', metaError);
                                    // Don't throw here - meta data is not critical for authentication
                                }

                                // Initialize Split.io after successful refresh with existing session
                                try {
                                    const { useSplitStore } = await import('@/stores/split');
                                    const splitStore = useSplitStore();
                                    await splitStore.initialize();
                                } catch (splitError) {
                                    console.error('Failed to initialize Split.io on page refresh:', splitError);
                                    // Don't throw here - Split.io is not critical for authentication
                                }

                                // Aggressively preload inbox modules after successful session refresh
                                // This ensures inbox loads instantly on page refresh
                                try {
                                    const { preloadInboxAfterLogin } = await import('@/utils/modulePreloader');
                                    preloadInboxAfterLogin().catch((preloadError) => {
                                        console.warn('Failed to preload inbox modules on refresh:', preloadError);
                                        // Don't throw - preloading is not critical for authentication
                                    });
                                } catch (importError) {
                                    console.warn('Failed to import modulePreloader on refresh:', importError);
                                }

                                // Prefetch JWT token for API Gateway after cookies are set
                                // This prevents race conditions when tasks are loaded immediately after page refresh
                                // This is now BLOCKING - we wait for the token before continuing
                                const { JWTHttpClient } = await import('@/services/JWTHttpClient');
                                const TASKS_SERVICE_URL = import.meta.env.VITE_TASKS_SERVICE_URL || '/service-tasks-stage';
                                const jwtClient = new JWTHttpClient(TASKS_SERVICE_URL);
                                
                                // Prefetch with retries (3 attempts with 500ms delay between)
                                // This will be shared across all JWTHttpClient instances
                                const tokenPrefetched = await jwtClient.prefetchToken(3, 500);
                                
                            if (!tokenPrefetched) {
                                console.error('JWT Token prefetch failed on refresh after retries - tasks may fail to load');
                            }
                            }
                        }
                    } catch (error) {
                        console.error('Failed to update user store:', error);
                    }
                    // Only send users to login when a session is lost. Calling handleAuthChange(false)
                    // on every anonymous userStatus would redirect public routes (e.g. /passwordchange).
                    if (!isAuthenticated.value && wasAuthenticated) {
                        handleAuthChange(false);
                    }
                }, 0);
            }

            if (data.csrf_token) {
                csrfToken.value = data.csrf_token;
                // Update global headers. Do NOT broadcast csrf-update from userStatus —
                // each tab may mint/receive its own token (esp. with per-tab client
                // instance ids). Cross-applying another tab's token causes
                // "Missing valid CSRF token" on later authenticated requests.
                updateBoomtownHeaders(csrfToken.value, clientInstanceId.value);
            } else {
                throw new Error('No CSRF token in response');
            }

            return data;
        } catch (error) {
            // Check for server errors (5xx status codes)
            if (error instanceof Error && error.message) {
                const httpErrorMatch = error.message.match(/HTTP error! status: (\d+)/);
                if (httpErrorMatch) {
                    const statusCode = parseInt(httpErrorMatch[1]);
                    if (statusCode >= 500 && statusCode < 600) {
                        reportServerError(statusCode, `userStatus API failed with ${statusCode} error`);
                    }
                }
            }

            if (error instanceof Error && error.message.includes('401')) {
                const wasAuthenticated = isAuthenticated.value;
                isAuthenticated.value = false;
                csrfToken.value = '';
                userEmail.value = '';

                if (wasAuthenticated) {
                    handleAuthChange(false);
                }
            } else {
                console.error('Failed to refresh token:', error);
                throw error;
            }
            return null;
        }
}

    // Start automatic token refresh
    let refreshInterval: number | null = null;

    function startTokenRefresh(): void {
        stopTokenRefresh(); // Clear any existing interval
        refreshInterval = window.setInterval(() => {
            refreshCsrfToken(false, true).catch(console.error);
        }, 60 * 1000); // Refresh every minute
    }

    function stopTokenRefresh(): void {
        if (refreshInterval) {
            clearInterval(refreshInterval);
            refreshInterval = null;
        }
    }

    /**
     * Login via Boomtown API (fetch/XHR). Must be called from the appv5 page with no full-page
     * navigation so the response that sets the session cookie is received while the document URL
     * is still appv5—otherwise the cookie is stored in the wrong partition and later API requests
     * get 401. HttpBaseClient uses credentials: 'include'; do not use a form action or redirect
     * flow that navigates to the API URL for login.
     */
    /**
     * Clear a session that authenticated but is not Admin/Superuser.
     * Does not broadcast logout to other tabs (use login-ended instead).
     */
    async function clearDeniedAdminSession(token: string): Promise<void> {
        isLoggingIn.value = false;
        isCompletingSsoLogin.value = false;
        broadcastAuthEvent('login-ended');

        if (token) {
            try {
                const headers: Record<string, string> = {
                    'x-boomtown-client-instance-id': clientInstanceId.value,
                    'x-request-id': uuidv4(),
                    'x-boomtown-csrf-token': token,
                    'content-type': 'application/json',
                };
                await baseHttpClient.get('admin/v4/core/', { sAction: 'userLogout' }, headers);
            } catch (error) {
                console.error('Failed to clear session after Admin access denial:', error);
            }
        }

        isAuthenticated.value = false;
        csrfToken.value = '';
        userEmail.value = '';
        updateBoomtownHeaders('', clientInstanceId.value);

        try {
            const userStore = useUserStore();
            userStore.clearUserData();
        } catch (error) {
            console.error('Failed to clear user data after Admin access denial:', error);
        }
    }

    async function login(credentials: LoginCredentials): Promise<LoginResponse> {
        const { clearSessionError, finishHandling401 } = useSessionStatus();
        
        // Mark that we're in the login process
        isLoggingIn.value = true;
        // Lock sibling login tabs so they can't submit a concurrent userLogin.
        broadcastAuthEvent('login-in-progress');
        
        // Clear any stale 401 handling state from previous session
        finishHandling401();
        
        const formData = new URLSearchParams();
        formData.append('email', credentials.email);
        formData.append('password', credentials.password);
        
        // Add rememberMe parameter if provided
        if (credentials.rememberMe) {
            formData.append('rememberMe', 'true');
        }

        try {
            // POST from appv5 origin with credentials: 'include' so Set-Cookie is in appv5 partition
            const data = await baseHttpClient.post<LoginResponse>(
                'admin/v4/core/index.php',
                formData,
                { sAction: 'userLogin' },
                { 'x-boomtown-client-instance-id': clientInstanceId.value }
            );

            if (data.csrf_token) {
                csrfToken.value = data.csrf_token;
                // Update global headers
                updateBoomtownHeaders(csrfToken.value, clientInstanceId.value)
            }

            if (data.success && data.authenticated) {
                // Only Admin/Superuser (role === 1) may use this app
                if (!isAdminSuperuser(data.users)) {
                    console.error('Login denied: user is not an Admin/Superuser', {
                        userId: data.users?.id,
                        role: data.users?.role,
                    });
                    await clearDeniedAdminSession(data.csrf_token || csrfToken.value);
                    throw new AdminAccessDeniedError();
                }

                // Set the email from the login response
                if (data.users?.email) {
                    userEmail.value = data.users.email;
                }

                // If we have editor key data, decode and store it
                if (data.editor_key) {
                    editorKey.value = Base64.decode(data.editor_key);
                }

                // Store all_access data if available
                if (data.all_access && Array.isArray(data.all_access)) {
                    allAccess.value = data.all_access;
                }

                if (data.users?.start_page && data.users?.start_page !== '') {
                    // Ignore potential anchor links, which are only supported in appv4, not cxme
                    if (!data.users.start_page.startsWith('#') && !data.users.start_page.startsWith('/#')) {
                        redirectAfterLogin.value = data.users.start_page;
                    }
                }

                // CRITICAL: Wait for browser to fully process Set-Cookie headers from login response.
                // Partitioned (CHIPS) cookies need time to commit; too short a delay can cause issueToken
                // and other post-login requests to run before the cookie is sent, leading to 401 / invalid_token.
                await new Promise(resolve => setTimeout(resolve, 250));

                // Set authentication state for navigation
                isAuthenticated.value = true;
                isInitialized.value = true; // Prevent router guard from calling initialize() again
                
                // Clear any session error state from previous 401s
                clearSessionError();

                // Update user store - defer access to avoid circular dependency.
                // Brief delay so partitioned cookie is committed before we hit Boomtown API (issueToken, etc.).
                setTimeout(async () => {
                    try {
                        const userStore = useUserStore();
                        
                        // Schema data available in login response
                        
                        userStore.persistUserData(data.users, data.perm_defs, data.users?._perms || null, data.xmpp, data.password_policy, data.users_group || null, data.screens_url || "", data.cdn_url || "", data.users_teams || null, data.card_fields_map || null, data.schema_fields_map || null, data.custom_case_fields || null, data.custom_location_fields || null, data.custom_member_fields || null, data.custom_users_fields || null);
                        
                        // Fetch current partner data after user data is loaded
                        if (data.users?.object_id) {
                            try {
                                const partnerStore = usePartnerStore();
                                await partnerStore.fetchCurrentPartner();
                            } catch (partnerError) {
                                console.error('Failed to fetch current partner data on login:', partnerError);
                                // Don't throw here - partner data is not critical for authentication
                            }
                        }

                        // Load meta data after successful login
                        try {
                            const metaStore = useMetaStore();
                            await Promise.all([
                                metaStore.loadMetaData(),
                                metaStore.loadPartnerMetaData()
                            ]);
                        } catch (metaError) {
                            console.error('Failed to load meta data on login:', metaError);
                            // Don't throw here - meta data is not critical for authentication
                        }

                        // Initialize Split.io after successful login
                        try {
                            const { useSplitStore } = await import('@/stores/split');
                            const splitStore = useSplitStore();
                            await splitStore.initialize();
                        } catch (splitError) {
                            console.error('Failed to initialize Split.io on login:', splitError);
                            // Don't throw here - Split.io is not critical for authentication
                        }

                        // Prefetch JWT token for API Gateway after cookies are set
                        // This prevents race conditions when tasks are loaded immediately after navigation
                        // Use exponential backoff for retries to handle cookie timing issues
                        // Wrap in try-catch to prevent hanging in test environments with mock limitations
                        try {
                            const { JWTHttpClient } = await import('@/services/JWTHttpClient');
                            const TASKS_SERVICE_URL = import.meta.env.VITE_TASKS_SERVICE_URL || '/service-tasks-stage';
                            const jwtClient = new JWTHttpClient(TASKS_SERVICE_URL);
                            
                            // Prefetch with retries using exponential backoff (500ms, 1000ms, 2000ms)
                            // This handles cases where cookies take longer to be available
                            let tokenPrefetched = false;
                            const maxRetries = 3;
                            
                            for (let attempt = 1; attempt <= maxRetries; attempt++) {
                                const retryDelay = 500 * Math.pow(2, attempt - 1); // 500ms, 1000ms, 2000ms
                                console.log(`Attempting JWT token prefetch (attempt ${attempt}/${maxRetries})...`);
                                
                                tokenPrefetched = await jwtClient.prefetchToken(1, 0); // Single attempt, no internal retry
                                
                                if (tokenPrefetched) {
                                    console.log(`✅ JWT token prefetch successful on attempt ${attempt}`);
                                    break;
                                }
                                
                                if (attempt < maxRetries) {
                                    console.warn(`JWT token prefetch attempt ${attempt} failed, waiting ${retryDelay}ms before retry...`);
                                    await new Promise(resolve => setTimeout(resolve, retryDelay));
                                }
                            }
                            
                            if (!tokenPrefetched) {
                                console.error('JWT Token prefetch failed after all retries - tasks may fail to load');
                                console.warn(
                                    '[Session] If tasks then return 401 invalid_token, the session cookie may not be sent to the Boomtown API (e.g. partitioned cookie not in same partition as appv5). ' +
                                    'In DevTools: Application → Cookies → select api.* → check relay cookie has Partition key = (https, appv5....). ' +
                                    'In Network: select the issueToken request and ensure Request Headers include Cookie.'
                                );
                                showAuthenticationError();
                            }
                        } catch (jwtPrefetchError) {
                            // Catch any errors from dynamic import or prefetch logic
                            // This prevents test environments or edge cases from hanging the login flow
                            console.warn('JWT token prefetch failed with error:', jwtPrefetchError);
                            // Don't throw - JWT prefetch is not critical for login success
                        }

                        // Aggressively preload inbox modules after successful login
                        // This ensures inbox loads instantly when the user navigates to it
                        try {
                            const { preloadInboxAfterLogin } = await import('@/utils/modulePreloader');
                            preloadInboxAfterLogin().catch((preloadError) => {
                                console.warn('Failed to preload inbox modules:', preloadError);
                                // Don't throw - preloading is not critical for authentication
                            });
                        } catch (importError) {
                            console.warn('Failed to import modulePreloader:', importError);
                        }

                        // Notify other tabs only AFTER post-login init finishes. Broadcasting
                        // earlier causes those tabs to reload + call userStatus, rotating CSRF
                        // while this tab still has in-flight inbox requests with the old token.
                        // Include the current CSRF so sibling tabs can adopt it before reload.
                        broadcastAuthEvent('login-success', csrfToken.value);
                        
                        // Keep isLoggingIn = true long enough for initial inbox API calls and for
                        // other tabs' delayed login-success reload to rotate/sync CSRF without
                        // this tab treating those 401s as a full session expiry.
                        loginInitTimeout = window.setTimeout(() => {
                            isLoggingIn.value = false;
                            loginInitTimeout = null;
                        }, POST_LOGIN_401_GRACE_MS);
                    } catch (error) {
                        console.error('Failed to update user store:', error);
                        isLoggingIn.value = false; // Clear flag on error
                    }
                }, 0);

                // Handle remember me functionality
                if (credentials.rememberMe) {
                    setRememberMeCookie();
                }

                startTokenRefresh();
                handleAuthChange(true);
            } else {
                // Credentials rejected or incomplete auth — unlock sibling login tabs.
                isLoggingIn.value = false;
                broadcastAuthEvent('login-ended');
            }

            return data;
        } catch (error) {
            // Clear login flag on error and unlock sibling login tabs
            isLoggingIn.value = false;
            broadcastAuthEvent('login-ended');
            // Re-throw the original error so callers can inspect the HTTP status code
            // (e.g. 403 for bad credentials is expected user behavior, not a system error)
            throw error;
        }
    }

    // Logout function
    async function logout(): Promise<void> {
        const { clearSessionError, finishHandling401 } = useSessionStatus();
        
        try {
            // Set logging out flag to prevent re-initialization
            isLoggingOut.value = true;
            
            // Cancel all in-flight API requests to prevent them from interfering with logout
            // Note: This cancels the client-side requests, but if they've already reached the server,
            // the server may still process them, which can leave the session in an inconsistent state.
            // A page refresh after login will resolve any issues.
            // baseHttpClient is the auth client only (userStatus/userLogout). Case/issues
            // traffic uses a separate HttpClient singleton — abort that too.
            baseHttpClient.cancelAllRequests();
            const { cancelAllHttpClientRequests } = await import('@/services/httpClientProvider');
            cancelAllHttpClientRequests();
            
            // Clear any pending login initialization timeout
            if (loginInitTimeout) {
                clearTimeout(loginInitTimeout);
                loginInitTimeout = null;
            }
            
            // Immediately set isLoggingIn to false to prevent stale state
            isLoggingIn.value = false;
            
            // Clear any in-progress 401 handling to prevent blocking next login
            finishHandling401();

            // Store current state but don't clear yet to avoid race condition
            const wasAuthenticated = Boolean(isAuthenticated.value);
            const currentToken = csrfToken.value;
            
            // Stop token refresh immediately to prevent interference
            stopTokenRefresh();

            // Clear JWT token cache and cancel its scheduled refresh timer so it
            // doesn't fire after logout and trigger a 401 on issueToken.
            const { JWTHttpClient } = await import('@/services/JWTHttpClient');
            JWTHttpClient.clearSharedTokenCache();

            // Make server logout call FIRST with current valid token
            if (wasAuthenticated && currentToken) {
                try {
                    const headers: Record<string, string> = {
                        'x-boomtown-client-instance-id': clientInstanceId.value,
                        'x-request-id': uuidv4(),
                        'x-boomtown-csrf-token': currentToken, // Use current token for authentication
                        'content-type': 'application/json',
                    };

                    // Call logout endpoint to clear server session
                    await baseHttpClient.get('admin/v4/core/', { sAction: 'userLogout' }, headers);
                } catch (error) {
                    console.error('Server logout failed, continuing with client cleanup:', error);
                    // Continue with client-side logout regardless
                }
            }

            // Notify other open tabs to also log out (client-side only — server session
            // is already cleared by the userLogout call above).
            markForceLoggedOut();
            broadcastAuthEvent('logout');

            // NOW clear all client-side state after server logout completes
            isAuthenticated.value = false;
            csrfToken.value = '';
            userEmail.value = '';
            isInitialized.value = false;
            editorKey.value = '';
            allAccess.value = [];

            // Clear any server error state and session error state on logout
            const { clearServerError } = useServerStatus();
            clearServerError();
            clearSessionError();

            // Note: Authentication cookies (relay, dstoken, etc.) are cleared by the server
            // via the userLogout endpoint above. We don't need to clear them manually here
            // as that can interfere with the server's cookie clearing process.

            // Clear user store data
            const userStore = useUserStore();
            userStore.clearUserData();

            // Clear partner store data
            const partnerStore = usePartnerStore();
            partnerStore.currentPartner = null;

            // Clear meta store data
            const metaStore = useMetaStore();
            metaStore.clearMetaData();

            // Clear global search and page history
            localStorage.removeItem('globalSearchHistory');
            localStorage.removeItem('pageHistory');
            
            // Also clear the stores' in-memory state
            try {
                const { useGlobalSearchStore } = await import('@/modules/search/stores/globalSearch');
                const { usePageHistoryStore } = await import('@/modules/search/stores/pageHistory');
                
                const globalSearchStore = useGlobalSearchStore();
                const pageHistoryStore = usePageHistoryStore();
                
                globalSearchStore.clearHistory();
                pageHistoryStore.clearHistory();
            } catch (error) {
                console.warn('Failed to clear search/page history stores:', error);
            }

            // Clear knowledge store — tree/library data is org-scoped and must not
            // carry over to the next session
            try {
                const { useKnowledgeStore } = await import('@/modules/knowledge/stores/knowledgeStore');
                const knowledgeStore = useKnowledgeStore();
                knowledgeStore.resetSessionState();
            } catch (error) {
                console.warn('Failed to clear knowledge store:', error);
            }

            // Clear all remaining org/user-scoped stores
            try {
                const [
                    { usePartnerStore },
                    { useTasksStore },
                    { useCasesStore },
                    { useCustomersStore },
                    { useContactsStore },
                    { useLocationsStore },
                    { useConnectorsStore },
                    { useUsersStore },
                    { useSummarizerStore },
                    { useCMSStore },
                    { useJourneysStore },
                    { useActionsStore },
                    { usePubsubEventsStore },
                    { usePartnerProductsStore },
                    { useSplitStore },
                ] = await Promise.all([
                    import('@/stores/partner'),
                    import('@/stores/tasks'),
                    import('@/stores/cases'),
                    import('@/stores/customersStore'),
                    import('@/stores/contactsStore'),
                    import('@/stores/locationsStore'),
                    import('@/stores/connectors'),
                    import('@/stores/users'),
                    import('@/stores/summarizer'),
                    import('@/stores/cms'),
                    import('@/stores/journeys'),
                    import('@/stores/actions'),
                    import('@/stores/pubsubEvents'),
                    import('@/stores/partnerProducts'),
                    import('@/stores/split'),
                ]);

                usePartnerStore().resetSessionState();
                useTasksStore().resetSessionState();
                useCasesStore().resetSessionState();
                useCustomersStore().resetSessionState();
                useContactsStore().resetSessionState();
                useLocationsStore().resetSessionState();
                useConnectorsStore().resetSessionState();
                useUsersStore().clearUsers();
                const summarizerStore = useSummarizerStore();
                summarizerStore.clearCustomerSummary();
                summarizerStore.clearContactSummary();
                summarizerStore.clearLocationSummary();
                useCMSStore().clearCache();
                useJourneysStore().clearJourneys();
                useActionsStore().reset();
                usePubsubEventsStore().clearEvents();
                usePartnerProductsStore().clearProducts();
                useSplitStore().destroy();
            } catch (error) {
                console.warn('Failed to clear session stores:', error);
            }

            // Clear comms and AI Studio stores (module-level, separate import path)
            try {
                const { useCommunicationsStore } = await import('@/modules/comms/stores/communications');
                useCommunicationsStore().resetSessionState();
            } catch (error) {
                console.warn('Failed to clear communications store:', error);
            }

            try {
                const { useAIStudioStore } = await import('@/modules/AIStudio/stores/aiStudioStore');
                useAIStudioStore().resetSessionState();
            } catch (error) {
                console.warn('Failed to clear AI Studio store:', error);
            }

            // Clear the in-session notification bell — at_mention, claim, and phone
            // notifications are per-session and should not carry over to the next login.
            try {
                const { useNotificationsStore } = await import('@/stores/notifications');
                useNotificationsStore().clearAll();
            } catch (error) {
                console.warn('Failed to clear notifications store:', error);
            }

            // Note: Don't call handleAuthChange(false) here during logout
            // as the calling component (RootLayout) handles the navigation

            // Update global headers
            updateBoomtownHeaders('', clientInstanceId.value)
        } catch (error) {
            console.error('Logout error:', error);
            // Still ensure we clear state but don't navigate during logout
            stopTokenRefresh();
        } finally {
            isAuthenticated.value = false;
            csrfToken.value = '';
            userEmail.value = '';
            isInitialized.value = false;
            editorKey.value = '';
            allAccess.value = [];
            // Clear the logging out flag after a delay to ensure navigation completes
            setTimeout(() => {
                isLoggingOut.value = false;
            }, 1000);
            // Update global headers
            updateBoomtownHeaders('', clientInstanceId.value)
        }
    }

    // Handle authentication state changes
    function handleAuthChange(isAuthenticatedFlag: boolean): void {
        if (!isAuthenticatedFlag) {
            if (isCompletingSsoLogin.value) {
                return;
            }
            router.push('/login');
        }
        // If authenticated, do not automatically redirect anywhere
    }

    /** Cancel in-flight API requests (e.g. SSO watchdog); base client only */
    function cancelInFlightAuthRequests(): void {
        baseHttpClient.cancelAllRequests();
    }

    // Set and clear acting-as impersonation
    function setActingAs(value: string): void {
        actingAs.value = value;
        sessionStorage.setItem('actingAs', value);
    }

    function clearActingAs(): void {
        actingAs.value = '';
        sessionStorage.removeItem('actingAs');
    }

    // Generate common HTTP headers with CSRF token
    function getCommonHeaders(): Record<string, string> {
        const headers: Record<string, string> = {
            'x-boomtown-client-instance-id': clientInstanceId.value,
            'x-request-id': uuidv4(),
            'content-type': 'application/json',
        };

        if (csrfToken.value) {
            headers['x-boomtown-csrf-token'] = csrfToken.value;
        }

        if (actingAs.value) {
            headers['Acting-As'] = actingAs.value;
        }

        return headers;
    }

    // Set remember me cookie
    function setRememberMeCookie(): void {
        // Set a cookie that expires in 30 days
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + 30);
        
        // Only use Secure flag in production (https)
        const isSecure = window.location.protocol === 'https:';
        const secureFlag = isSecure ? '; Secure' : '';
        
        document.cookie = `remember_me=true; expires=${expirationDate.toUTCString()}; path=/; SameSite=Strict${secureFlag}`;
    }

    // Clear remember me cookie
    function clearRememberMeCookie(): void {
        document.cookie = 'remember_me=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    }

    // Check if remember me cookie exists
    function hasRememberMeCookie(): boolean {
        const cookies = document.cookie.split(';');
        const hasRememberMe = cookies.some(cookie => cookie.trim().startsWith('remember_me=true'));
        return hasRememberMe;
    }

    async function userResetPassword(credentials: resetCredentials): Promise<ResetPasswordResponse> {
        const formData = new URLSearchParams()
        formData.append('email', credentials.email)

        try {
            const data = await baseHttpClient.post<ResetPasswordResponse>(
                'admin/v4/core/',
                formData,
                { sAction: 'userResetPassword' },
                {
                  'Content-Type': 'application/x-www-form-urlencoded',
                  'x-boomtown-client-instance-id': clientInstanceId.value,
                  'x-boomtown-app': 'cxme',
                }
              );

          // if your reset endpoint also returns a new CSRF token, grab it
          if ((data as any).csrf_token) {
            const token = (data as any).csrf_token as string
            updateBoomtownHeaders(token, clientInstanceId.value)
          }

          return data
        } catch (err) {
          console.error('Password reset request failed:', err)
          // you can throw a more specific error if you like:
          throw new Error(
            err instanceof Error
              ? err.message
              : 'Request to reset password failed'
          )
        }
      }

      async function userChangePassword(credentials: changeCredentials): Promise<ResetPasswordResponse> {
        const formData = new URLSearchParams()
        formData.append('email', credentials.email)
        formData.append('password', credentials.password)
        formData.append('confirm_password', credentials.confirm_password)
        formData.append('token', credentials.token)

        try {
            const data = await baseHttpClient.post<ResetPasswordResponse>(
                'admin/v4/core/',
                formData,
                { sAction: 'userChangePassword' },
                {
                  'Content-Type': 'application/x-www-form-urlencoded',
                  'x-boomtown-client-instance-id': clientInstanceId.value,
                  'x-boomtown-app': 'cxme',
                }
              );

          // if your reset endpoint also returns a new CSRF token, grab it
          if ((data as any).csrf_token) {
            const token = (data as any).csrf_token as string
            updateBoomtownHeaders(token, clientInstanceId.value)
          }

          return data
        } catch (err) {
          console.error('Password change request failed:', err)
          // you can throw a more specific error if you like:
          throw new Error(
            err instanceof Error
              ? err.message
              : 'Request to change password failed'
          )
        }
      }


    /**
     * Called when another tab has already completed a full logout (server session
     * cleared there). This tab only needs to wipe its own client-side state and
     * navigate away — skipping the server `userLogout` call.
     * Uses a full-page navigation instead of `router.push` so Pinia/memory state
     * is completely reset in this tab.
     */
    async function handleRemoteLogout(): Promise<void> {
        if (isLoggingOut.value) return;
        isLoggingOut.value = true;
        isLoggingIn.value = false;
        stopTokenRefresh();

        baseHttpClient.cancelAllRequests();
        const { cancelAllHttpClientRequests } = await import('@/services/httpClientProvider');
        cancelAllHttpClientRequests();

        // Prevent /login from bouncing back to home if userStatus still briefly
        // reports authenticated (cookie clear race / failed origin userLogout).
        markForceLoggedOut();

        const tokenForLogout = csrfToken.value;

        try {
            const { JWTHttpClient } = await import('@/services/JWTHttpClient');
            JWTHttpClient.clearSharedTokenCache();
        } catch { /* non-critical */ }

        // Belt-and-suspenders: clear the server session from this tab too when we
        // still have a CSRF token. Harmless if the origin tab already cleared it.
        if (tokenForLogout) {
            try {
                await baseHttpClient.get(
                    'admin/v4/core/',
                    { sAction: 'userLogout' },
                    {
                        'x-boomtown-client-instance-id': clientInstanceId.value,
                        'x-request-id': uuidv4(),
                        'x-boomtown-csrf-token': tokenForLogout,
                        'content-type': 'application/json',
                    },
                );
            } catch {
                // Cookie may already be gone — continue to local cleanup.
            }
        }

        isAuthenticated.value = false;
        csrfToken.value = '';
        userEmail.value = '';
        isInitialized.value = false;
        editorKey.value = '';
        allAccess.value = [];

        updateBoomtownHeaders('', clientInstanceId.value);

        // Full-page navigation ensures Pinia stores are fully reset in this tab.
        window.location.assign('/login');
    }

    /**
     * Intentionally a no-op. Cross-tab CSRF apply overwrites a tab's valid
     * per-instance token with another tab's token and causes later 401s
     * ("Missing valid CSRF token"). app-v4 keeps CSRF in-memory per tab only.
     * Kept as a stub so existing csrf-update listeners remain harmless.
     */
    function applyRemoteCsrfToken(_token: string): void {
        // no-op — see comment above
    }

    /**
     * Whether 401 handlers should skip logout. Covers login transitions and the
     * window after another tab rotates the shared CSRF token.
     */
    function shouldSuppress401Logout(): boolean {
        return isLoggingIn.value || isLoggingOut.value || isCompletingSsoLogin.value
            || Date.now() < csrfSyncGraceUntil
            || isRecoveringSession;
    }

    /**
     * Attempt to recover from a likely CSRF race by refreshing userStatus once.
     * Returns true if the session is still valid after refresh (caller should NOT logout).
     */
    async function tryRecoverSession(): Promise<boolean> {
        if (!isAuthenticated.value || isLoggingOut.value || isRecoveringSession) {
            return false;
        }
        isRecoveringSession = true;
        csrfSyncGraceUntil = Date.now() + CSRF_SYNC_GRACE_MS;
        try {
            const data = await refreshCsrfToken(true, true);
            if (data?.authenticated && data.csrf_token) {
                return true;
            }
            return false;
        } catch (error) {
            console.warn('Session recovery after 401 failed:', error);
            return false;
        } finally {
            isRecoveringSession = false;
        }
    }

    return {
        isAuthenticated,
        isInitialized,
        isLoggingOut,
        isLoggingIn,
        isCompletingSsoLogin,
        csrfToken,
        clientInstanceId,
        apiBaseUrl,
        userEmail,
        editorKey,
        allAccess,
        actingAs,
        initialize,
        login,
        logout,
        refreshCsrfToken,
        getCommonHeaders,
        setActingAs,
        clearActingAs,
        userResetPassword,
        userChangePassword,
        setRememberMeCookie,
        clearRememberMeCookie,
        hasRememberMeCookie,
        redirectAfterLogin,
        cancelInFlightAuthRequests,
        handleRemoteLogout,
        applyRemoteCsrfToken,
        shouldSuppress401Logout,
        tryRecoverSession,
    };
});
